import React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Checkbox, FormError, TextInput } from "../../../components";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import { Trans } from "react-i18next";
import { Link } from "react-router";
import { FormSubmitHandler } from "../../../common/models";
import {
  passwordComplexityValidation,
  samePasswordCheck,
  useValidators,
} from "../../../helpers/validation";
import { handleFormServerErrors } from "../../../helpers/errorHandling";
import SubmitButton from "../../../components/forms/SubmitButton";

interface Props {
  onSubmit: FormSubmitHandler<CreateAccountFormValues>;
}

export interface CreateAccountFormValues {
  email: string;
  password: string;
  passwordAgain: string;
}

const CreateAccountForm: React.FC<Props> = (props): React.ReactElement => {
  const { tInvite, tInput, tError } = useCustomTranslation();
  const validators = useValidators();

  const methods = useForm<CreateAccountFormValues>({
    defaultValues: {
      email: "",
      password: "",
      passwordAgain: "",
    },
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<CreateAccountFormValues> = async (data) => {
    try {
      await props.onSubmit(data);
    } catch (e) {
      handleFormServerErrors(e, tError, methods.setError);
    }
  };

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="sm:w-[480px]">
        <TextInput
          name="email"
          placeholder={tInput("email_input")}
          type="email"
          required
          options={{ required: validators.required }}
        />
        <TextInput
          name="password"
          placeholder={tInput("password_input")}
          type="password"
          required
          options={{
            required: validators.required,
            minLength: validators.minLength(8),
            validate: {
              passwordComplexityValidation,
            },
          }}
        />
        <TextInput
          name="passwordAgain"
          placeholder={tInput("password_again_input")}
          type="password"
          required
          options={{
            required: validators.required,
            minLength: validators.minLength(8),
            validate: {
              passwordComplexityValidation,
              samePasswordCheck: (value, values) =>
                samePasswordCheck(value as string, values.password as string),
            },
          }}
        />
        <p className="text-gray-dark mt-2 mb-2 text-[14px]/[24px] tracking-[-0.14px]">
          {tInput("password_complex_required")}
        </p>
        <Checkbox
          name="conditions"
          required
          options={{ required: validators.required }}
        >
          <Trans i18nKey={"conditions"} ns="customerportal-common">
            Souhlasím s
            <Link to="https://www.mwork365.com/vop/" className="underline">
              Podmínkami poskytování
            </Link>
            , Smlouvou o
            <Link to="https://www.mwork365.com/soukromi/" className="underline">
              zpracování osobních údajů a Zásadami ochrany osobních údajů
            </Link>
          </Trans>
        </Checkbox>
        <FormError />
        <SubmitButton
          className="mt-6 self-start"
          label={tInvite("create_and_accept_button")}
          icon="arrow-right"
        />
      </form>
    </FormProvider>
  );
};

export default CreateAccountForm;
